<!--
  Cambio indirizzo di una tratta (pickup o transfer), solo Simba.

  Sul PICKUP si cambia solo l'indirizzo del cliente: l'altro capo è sempre la
  sede. Sul TRANSFER partenza e/o arrivo.

  Prima si CALCOLA (PATCH /booking/:id/leg-address con `dryRun`): il backend
  rifà km, fascia, prezzo e turni con gli stessi calcoli della creazione e
  restituisce il confronto prima/dopo. Si salva solo dopo averlo visto. Chi è
  assegnato resta sulla tratta; se con i nuovi orari non è più libero lo dice
  il calcolo, e si può scegliere di liberare la tratta.
-->
<template>
  <el-dialog
    :model-value="modelValue"
    :title="leg?.direction === 'return' ? 'Cambia indirizzo del ritorno' : 'Cambia indirizzo dell\'andata'"
    width="640px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div
      v-if="leg"
      class="lad"
    >
      <div
        v-for="end in capi"
        :key="end.key"
        class="lad__end"
      >
        <span class="lad__lbl">{{ end.label }}</span>
        <div class="lad__addr">
          <el-input
            v-model="punti[end.key].address"
            placeholder="Via/piazza"
            class="lad__street"
            @input="toccato(end.key)"
          />
          <el-input
            v-model="punti[end.key].street_number"
            placeholder="N°"
            class="lad__n"
            @input="toccato(end.key)"
          />
          <el-input
            v-model="punti[end.key].cap"
            placeholder="CAP"
            class="lad__cap"
            @input="toccato(end.key)"
          />
          <el-input
            v-model="punti[end.key].city"
            placeholder="Città"
            class="lad__city"
            @input="toccato(end.key)"
          />
          <el-input
            v-model="punti[end.key].province"
            placeholder="PR"
            class="lad__pr"
            @input="toccato(end.key)"
          />
        </div>
        <p
          v-if="punti[end.key].label"
          class="lad__hint lad__hint--ok"
        >
          ✓ {{ punti[end.key].label }}
        </p>
        <p
          v-else-if="punti[end.key].geoError"
          class="lad__hint lad__hint--attn"
        >
          {{ punti[end.key].geoError }}
        </p>
      </div>

      <!-- ---- confronto prima / dopo ---- -->
      <div
        v-if="anteprima"
        class="lad__cmp"
      >
        <div class="lad__row lad__row--head">
          <span />
          <span>Prima</span>
          <span>Dopo</span>
        </div>
        <div class="lad__row">
          <span>Km</span>
          <span>{{ numero(anteprima.km.prima) }}</span>
          <b :class="{ 'lad__chg': cambia(anteprima.km) }">{{ numero(anteprima.km.dopo) }}</b>
        </div>
        <div class="lad__row">
          <span>Fascia</span>
          <span>{{ fascia(anteprima.fascia.prima) }}</span>
          <b :class="{ 'lad__chg': cambia(anteprima.fascia) }">{{ fascia(anteprima.fascia.dopo) }}</b>
        </div>
        <div class="lad__row">
          <span>Prezzo della tratta</span>
          <span>{{ euro(anteprima.prezzoTratta.prima) }}</span>
          <b :class="{ 'lad__chg': cambia(anteprima.prezzoTratta) }">{{ euro(anteprima.prezzoTratta.dopo) }}</b>
        </div>
        <div class="lad__row">
          <span>Imponibile prenotazione</span>
          <span>{{ euro(anteprima.prenotazione.imponibile.prima) }}</span>
          <b :class="{ 'lad__chg': cambia(anteprima.prenotazione.imponibile) }">{{ euro(anteprima.prenotazione.imponibile.dopo) }}</b>
        </div>
        <div class="lad__row">
          <span>Totale fattura</span>
          <span>{{ euro(anteprima.prenotazione.totale.prima) }}</span>
          <b :class="{ 'lad__chg': cambia(anteprima.prenotazione.totale) }">{{ euro(anteprima.prenotazione.totale.dopo) }}</b>
        </div>
        <template v-if="anteprima.turni">
          <div
            v-for="t in turni"
            :key="t.ruolo"
            class="lad__row"
          >
            <span>{{ t.ruolo }}<em v-if="t.persona"> · {{ t.persona }}</em><em v-else> · da assegnare</em></span>
            <span>{{ orario(t.prima) }}</span>
            <b :class="{ 'lad__chg': orario(t.prima) !== orario(t.dopo) }">{{ orario(t.dopo) }}</b>
          </div>
        </template>

        <p
          v-if="anteprima.prezzoManuale"
          class="lad__hint"
        >
          Il prezzo di questo Transfer è deciso a mano: cambiano i km, il prezzo resta.
        </p>
        <p
          v-if="anteprima.valet"
          class="lad__hint"
        >
          Corsa col valet: il prezzo è l'eccedenza km del pacchetto.
        </p>
        <p
          v-if="anteprima.importiCod3"
          class="lad__hint lad__hint--attn"
        >
          Nel COD3 questa prenotazione ha un importo corretto a mano: vale quello.
        </p>
        <div
          v-if="anteprima.motivo || liberaDriver"
          class="lad__warn"
        >
          <p v-if="anteprima.motivo">
            Con il nuovo indirizzo {{ anteprima.motivo }}.
          </p>
          <el-checkbox
            v-model="liberaDriver"
            @change="calcola"
          >
            Togli {{ assegnatiTesto }} dalla tratta e ricalcola con chi è libero
          </el-checkbox>
        </div>
      </div>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">
        Annulla
      </el-button>
      <el-button
        :disabled="!modificato"
        :loading="calcolando"
        @click="calcola"
      >
        Calcola
      </el-button>
      <el-button
        type="primary"
        :disabled="!anteprima || !!anteprima.motivo"
        :loading="salvando"
        @click="salva"
      >
        Salva
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import bookingAPI from '../../services/bookingAPI.js';
import prenotazioneAPI from '../../services/prenotazioneAPI.js';

const vuoto = () => ({
  address: '', street_number: '', cap: '', city: '', province: '',
  latitude: 0, longitude: 0, label: '', geoError: '', geocoding: false, originale: '',
});
const testo = (p) => [p?.address, p?.street_number, p?.cap, p?.city, p?.province]
  .map((v) => String(v ?? '').trim().toLowerCase()).join('|');

export default {
  name: 'LegAddressDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    bookingId: { type: String, default: '' },
    leg: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const punti = reactive({ origin: vuoto(), destination: vuoto() });
    const anteprima = ref(null);
    const calcolando = ref(false);
    const salvando = ref(false);
    const liberaDriver = ref(false);

    // Sul pickup l'origine è la sede: si cambia solo l'indirizzo del cliente.
    const capi = computed(() => (props.leg?.kind === 'pickup'
      ? [{ key: 'destination', label: 'Indirizzo del cliente' }]
      : [{ key: 'origin', label: 'Partenza' }, { key: 'destination', label: 'Arrivo' }]));

    const carica = (key, p) => {
      Object.assign(punti[key], vuoto(), {
        address: p?.address || '',
        street_number: String(p?.street_number ?? ''),
        cap: String(p?.cap ?? ''),
        city: p?.city || '',
        province: p?.province || '',
        latitude: Number(p?.latitude) || 0,
        longitude: Number(p?.longitude) || 0,
      });
      punti[key].originale = testo(punti[key]);
    };

    watch(() => [props.modelValue, props.leg], () => {
      if (!props.modelValue || !props.leg) return;
      carica('origin', props.leg.originPoint);
      carica('destination', props.leg.destinationPoint);
      anteprima.value = null;
      liberaDriver.value = false;
    }, { immediate: true });

    const cambiati = computed(() => capi.value
      .map((c) => c.key)
      .filter((k) => testo(punti[k]) !== punti[k].originale));
    const modificato = computed(() => cambiati.value.length > 0);

    // Un indirizzo cambiato invalida il calcolo e le coordinate.
    const toccato = (key) => {
      anteprima.value = null;
      Object.assign(punti[key], { latitude: 0, longitude: 0, label: '', geoError: '' });
    };

    const geocodifica = async (key) => {
      const p = punti[key];
      if (!p.address || !p.city) {
        p.geoError = 'Servono almeno via e città.';
        return false;
      }
      if (p.latitude && p.longitude) return true;
      p.geocoding = true;
      try {
        const r = await prenotazioneAPI.getPoints({
          text: `${p.address} ${p.street_number} ${p.cap}, ${p.city} ${p.province}`,
        });
        const hit = (r.data || [])[0];
        if (!hit?.point?.latitude) {
          p.geoError = 'Indirizzo non trovato: controlla via, CAP e città.';
          return false;
        }
        p.label = hit.label;
        p.latitude = hit.point.latitude;
        p.longitude = hit.point.longitude;
        return true;
      } catch (e) {
        p.geoError = 'Geocodifica non riuscita.';
        return false;
      } finally {
        p.geocoding = false;
      }
    };

    const corpo = (dryRun) => {
      const out = { legId: props.leg.id, dryRun, liberaDriver: liberaDriver.value };
      for (const k of cambiati.value) {
        const p = punti[k];
        out[k] = {
          address: p.address.trim(),
          street_number: String(p.street_number).trim(),
          cap: String(p.cap).trim(),
          city: p.city.trim(),
          province: p.province.trim(),
          latitude: Number(p.latitude),
          longitude: Number(p.longitude),
        };
      }
      return out;
    };

    const calcola = async () => {
      if (!modificato.value) return;
      calcolando.value = true;
      try {
        for (const k of cambiati.value) {
          if (!(await geocodifica(k))) return;
        }
        const r = await bookingAPI.changeLegAddress(props.bookingId, corpo(true));
        anteprima.value = r.data;
      } catch (e) {
        // il messaggio del server lo mostra l'interceptor di API.js
        console.error('Calcolo non riuscito:', e?.response?.status);
        anteprima.value = null;
      } finally {
        calcolando.value = false;
      }
    };

    const salva = async () => {
      salvando.value = true;
      try {
        await bookingAPI.changeLegAddress(props.bookingId, corpo(false));
        ElMessage.success(anteprima.value?.assegnati?.driver && !liberaDriver.value
          ? 'Indirizzo cambiato, driver avvisati.'
          : 'Indirizzo cambiato.');
        emit('update:modelValue', false);
        emit('saved');
      } catch (e) {
        console.error('Cambio indirizzo non salvato:', e?.response?.status);
      } finally {
        salvando.value = false;
      }
    };

    const turni = computed(() => {
      const t = anteprima.value?.turni || {};
      return [
        t.driver && { ruolo: 'Driver', ...t.driver },
        t.jolly && { ruolo: 'Jolly', ...t.jolly },
      ].filter(Boolean);
    });
    const assegnatiTesto = computed(() => {
      const a = anteprima.value?.assegnati || {};
      const nomi = [a.driver, a.jolly].filter(Boolean);
      return nomi.length ? nomi.join(' e ') : 'i driver';
    });

    const hhmm = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime())
        ? '—'
        : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const orario = (x) => (x ? `${hhmm(x.inizio)}–${hhmm(x.fine)}` : '—');
    const euro = (n) => (n == null ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`);
    const numero = (n) => (n == null ? '—' : String(Math.round(Number(n) * 10) / 10).replace('.', ','));
    const fascia = (n) => (n == null ? '—' : `fino a ${numero(n)} km`);
    const cambia = (x) => x && String(x.prima) !== String(x.dopo);

    return {
      punti, capi, anteprima, calcolando, salvando, liberaDriver, modificato, turni, assegnatiTesto,
      toccato, calcola, salva, orario, euro, numero, fascia, cambia,
    };
  },
};
</script>

<style scoped>
.lad { display: flex; flex-direction: column; gap: 14px; }
.lad__lbl {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.lad__addr { display: grid; grid-template-columns: 1fr 64px 76px 1fr 52px; gap: 6px; }
.lad__hint { margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary); }
.lad__hint--ok { color: var(--state-ok); }
.lad__hint--attn { color: var(--state-attn); }
.lad__cmp { border-top: 1px solid var(--border-color); padding-top: 8px; }
.lad__row {
  display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 10px; padding: 6px 0;
  font-size: 13px; border-bottom: 1px solid var(--border-color);
  font-variant-numeric: tabular-nums; color: var(--text-secondary);
}
.lad__row em { font-style: normal; color: var(--text-tertiary); }
.lad__row b { color: var(--text-primary); font-weight: 600; }
.lad__row--head {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.lad__chg { color: var(--primary) !important; }
.lad__warn {
  margin-top: 10px; padding: 10px 12px; border-radius: 6px;
  background: var(--state-attn-bg); border: 1px solid var(--state-attn-line);
  color: var(--state-attn); font-size: 12.5px;
}
.lad__warn p { margin: 0 0 6px; }
@media (max-width: 640px) {
  .lad__addr { grid-template-columns: 1fr 1fr; }
}
</style>
